import { createContext, FC, PropsWithChildren, useContext } from "react";
import type { Address } from "rujira.js";

/**
 * An account layer can put an app-layer account in front of the connected
 * wallet, e.g. a sub-account the wallet owns (see `Account.owner`). Features
 * then act as that account: it is `useAccounts().selected`. The wallet list,
 * selection, connecting and signing stay the wallets' (`useWalletAccounts`).
 */
export type AccountLayer = {
  /** The account acting for the wallet; missing while unavailable (locked). */
  account?: { address: Address; owner?: string };
  /**
   * The account must stay unlinked from the connected wallets: nothing it
   * does pays one of them, even while the account itself is unavailable.
   */
  unlinked?: boolean;
};

const LayerContext = createContext<AccountLayer | undefined>(undefined);

export const AccountLayerProvider: FC<
  PropsWithChildren<{ layer?: AccountLayer }>
> = ({ layer, children }) => (
  <LayerContext.Provider value={layer}>{children}</LayerContext.Provider>
);

export const useAccountLayer = (): AccountLayer | undefined =>
  useContext(LayerContext);
